import { useEffect, useMemo, useState } from "react";

import {
  Box,
  InputFilterText,
  SelectFilterInput,
  Checkbox,
  Button,
  Icon,
  Alert,
  Spinner,
  AlertTitle,
  Heading,
} from "@ogt/ui";

import ResultList from "src/components/ResultList";
import type { Timetable } from "../types/FormTypes.js";

type MunicipalityOption = {
  label: string;
  value: string;
};

type TrafficType = {
  Key: string;
  Name: string;
  CssClass?: string;
  IconCssClass?: string;
};

type LineApiPeriodLeaf = {
  Heading: string;
  PdfUrlPath: string;
  PathAccessibility?: string;
  FromDate?: string;
  ToDate?: string;
};

type LineApiPeriodContainer = {
  DateIntervalId?: number;
  TimeTablePeriodId?: number;
  MultiTimeTablePeriodsContainingThis?: LineApiPeriodLeaf[];
} & Partial<LineApiPeriodLeaf>;

type LineApiItem = {
  LineNrReal: number;

  LineName?: string;

  LineNameFromXml?: string;

  Heading?: string;

  Municipalities?: {
    Id: number;
    MunicipalityName: string;
    UrlSegment: string;
  }[];

  LineType?: {
    Key?: string;
    Label?: string;
  };

  LinePeriods?: Record<
    string,
    LineApiPeriodContainer
  >;

  Ogt?: {
    OgtMunicipalities?: {
      Id: number;
      MunicipalityName: string;
      UrlSegment: string;
    }[];

    OgtTrafficTypeGroupKey?: string;

    OgtTrafficTypeIconCssClass?: string;

    OgtTrafficTypeGroupCssClass?: string;

    OgtLineDefiningAttribute?: string;
  };
};
type LineSearchItem = {
  LineNrReal: number;

  Ogt?: {
    OgtMunicipalities?: {
      Id: number;
      MunicipalityName: string;
      UrlSegment: string;
    }[];

    OgtTrafficTypeGroupKey?: string;

    OgtTrafficTypeIconCssClass?: string;

    OgtTrafficTypeGroupCssClass?: string;

    OgtLineDefiningAttribute?: string;
  };
};



export default function LineSearch() {
  const apiBaseUrl =
    process.env.REACT_APP_TPS_API_BASE_URL?.replace(/\/$/, "") ?? "";

  const [lineNumber, setLineNumber] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("all");
  const [trafficTypes, setTrafficTypes] = useState<TrafficType[]>([]);

  useEffect(() => {
    const ac = new AbortController();

    fetchJson<TrafficType[]>(
      `${apiBaseUrl}/api/v1/TrafficTypes/TimetableFilters`,
      ac.signal
    )
      .then((items) => {
        setTrafficTypes(items);

        setSelectedTrafficTypes(
          items.map((t) => t.Key)
        );
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error(err);
        }
      });

    return () => ac.abort();
  }, [apiBaseUrl]);

  useEffect(() => {
    const ac = new AbortController();

    fetchJson<LineSearchItem[]>(
      `${apiBaseUrl}/api/v1/Lines?onlyProperLines=true`,
      ac.signal
    )
      .then((items) => {
        const municipalities = items.flatMap(
          (line) => line.Ogt?.OgtMunicipalities ?? []
        );

        const unique = municipalities.filter(
          (m, index, arr) =>
            arr.findIndex(
              (x) => x.UrlSegment === m.UrlSegment
            ) === index
        );

        const options: MunicipalityOption[] = [
          {
            label: "Alla kommuner",
            value: "all",
          },

          ...unique
            .map((m) => ({
              label: m.MunicipalityName,
              value: String(m.Id),
            }))
            .sort((a, b) =>
              a.label.localeCompare(b.label, "sv")
            ),
        ];

        setMunicipalityOptions(options);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error(err);
        }
      });
    return () => ac.abort();
  }, [apiBaseUrl]);

  const [selectedTrafficTypes, setSelectedTrafficTypes] =
    useState<string[]>([]);

  const [municipalityOptions, setMunicipalityOptions] = useState<
    MunicipalityOption[]
  >([
    {
      label: "Alla kommuner",
      value: "all",
    },
  ]);

  const [allTimetables, setAllTimetables] = useState<Timetable[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [liveMessage, setLiveMessage] = useState("");

  const [searchParams, setSearchParams] = useState({
    lineNumber: "",
    municipality: "",
    trafficTypes: [] as string[],
  });

  async function fetchJson<T>(
    url: string,
    signal?: AbortSignal
  ): Promise<T> {
    const res = await fetch(url, {
      headers: {
        Accept: "application/json",
      },
      signal,
    });

    if (!res.ok) {
      throw new Error(`${res.status} ${res.statusText}`);
    }

    return res.json() as Promise<T>;
  }

  function parseLocalDate(s?: string): Date | null {
    if (!s) return null;

    const m = s.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/
    );

    if (m) {
      const [, y, mo, d, hh = "00", mm = "00", ss = "00"] = m;

      return new Date(
        Number(y),
        Number(mo) - 1,
        Number(d),
        Number(hh),
        Number(mm),
        Number(ss),
        0
      );
    }

    const d = new Date(s);

    return isNaN(d.getTime()) ? null : d;
  }

  function isPeriodExpired(
    p: LineApiPeriodContainer,
    now = new Date()
  ): boolean {
    const to = parseLocalDate(p.ToDate);

    if (!to) return false;

    to.setHours(23, 59, 59, 999);

    return to < now;
  }

  function mapApiToTimetables(
    items: LineApiItem[],
    apiBaseUrl: string
  ): Timetable[] {
    const now = new Date();

    return items.map((line) => {
      const periods = Object.values(line.LinePeriods ?? {})
        .filter((p) => !isPeriodExpired(p, now))
        .sort((a, b) => {
          const ta = parseLocalDate(a.FromDate)?.getTime() ?? 0;
          const tb = parseLocalDate(b.FromDate)?.getTime() ?? 0;

          return ta - tb;
        });

      const pdfs = periods
        .filter(
          (p) =>
            !!p.PdfUrlPath ||
            !!p.MultiTimeTablePeriodsContainingThis?.[0]?.PdfUrlPath
        )
        .map((p) => {
          const collection = p.MultiTimeTablePeriodsContainingThis?.[0];

          return {
            heading: p.Heading ?? "",
            pdfUrl: apiBaseUrl + (p.PdfUrlPath ?? ""),

            accessiblePdfUrl: p.PathAccessibility
              ? apiBaseUrl + p.PathAccessibility
              : undefined,

            ValidFrom: p.FromDate,
            ValidTo: p.ToDate,

            collectionPdf: collection?.PdfUrlPath
              ? {
                heading: collection.Heading ?? "",
                pdfUrl: apiBaseUrl + collection.PdfUrlPath,

                accessiblePdfUrl: collection.PathAccessibility
                  ? apiBaseUrl + collection.PathAccessibility
                  : undefined,

                ValidFrom: collection.FromDate,
                ValidTo: collection.ToDate,
              }
              : undefined,
          };
        });

      const municipalities =
        line.Ogt?.OgtMunicipalities?.map((m) => ({
          name: m.MunicipalityName,
          slug: m.UrlSegment,
        })) ?? [];

      return {
        id: String(line.LineNrReal),

        line:
          line.LineName ??
          line.LineNameFromXml ??
          "",

        lineNrReal: line.LineNrReal,

        municipality: municipalities
          .map((m) => m.name)
          .join(", "),

        municipalitySlug:
          municipalities[0]?.slug ?? "",

        municipalities,

        trafficType:
          line.LineType?.Label ?? "",

        trafficTypeKey:
          line.Ogt?.OgtTrafficTypeGroupKey ?? "",

        route: line.Heading ?? "",

        trafficTypeName:
          line.LineType?.Label ?? "",

        trafficTypeIconCssClass:
          line.Ogt?.OgtTrafficTypeIconCssClass ?? "",

        trafficTypeGroupCssClass:
          line.Ogt?.OgtTrafficTypeGroupCssClass ?? "",

        pdfs,
      };
    });
  }

  useEffect(() => {
    if (trafficTypes.length === 0) return;

    const params = new URLSearchParams(window.location.search);

    const line = params.get("lineNumber") ?? "";
    const muni = params.get("municipality") ?? "all";
    const traffic = params.get("trafficTypes")
      ? params.get("trafficTypes")!.split(",")
      : trafficTypes.map((t) => t.Key);

    setLineNumber(line);
    setSelectedMunicipality(muni);
    setSelectedTrafficTypes(traffic);

    const hasUrlSearch =
      line.trim() !== "" ||
      muni !== "all" ||
      params.has("trafficTypes");

    if (hasUrlSearch) {
      handleSearch({
        lineNumber: line,
        municipality: muni,
        trafficTypes: traffic,
        skipUrlUpdate: true,
      });
    }
  }, [trafficTypes]);

  async function handleSearch(overrides?: {
    lineNumber?: string;
    municipality?: string;
    trafficTypes?: string[];
    skipUrlUpdate?: boolean;
  }) {

    try {
      const params = new URLSearchParams();

      params.set("onlyProperLines", "true");

      const ln =
        overrides?.lineNumber ?? lineNumber;

      const muni =
        overrides?.municipality ??
        selectedMunicipality;

      const traffic =
        overrides?.trafficTypes ??
        selectedTrafficTypes;

      if (ln.trim()) {
        params.set("query", ln.trim());
      }

      if (traffic.length > 0) {
        params.set(
          "trafficTypeFilters",
          traffic.join(",")
        );
      }

      if (muni && muni !== "all") {
        params.set("municipalities", muni);
      }

      const searchUrl =
        `${apiBaseUrl}/api/v1/Lines?${params.toString()}`;

      const searchItems =
        await fetchJson<LineSearchItem[]>(searchUrl);

      const ids = searchItems.map((x) => x.LineNrReal);

      if (ids.length === 0) {
        setAllTimetables([]);
        setHasSearched(true);
        return;
      }

      const detailsUrl =
        `${apiBaseUrl}/api/v1/Lines/LinesDetails?lineIds=${ids.join(",")}`;

      const details =
        await fetchJson<LineApiItem[]>(detailsUrl);

      const merged = details.map((detail) => {
        const searchItem = searchItems.find(
          (x) => x.LineNrReal === detail.LineNrReal
        );

        return {
          ...detail,
          Ogt: searchItem?.Ogt,
        };
      });

      const mapped =
        mapApiToTimetables(merged, apiBaseUrl);

      setAllTimetables(mapped);

      setSearchParams({
        lineNumber: ln,
        municipality: muni,
        trafficTypes: traffic,
      });

      if (!overrides?.skipUrlUpdate) {
        const urlParams = new URLSearchParams();

        if (ln.trim()) {
          urlParams.set("lineNumber", ln.trim());
        }

        if (muni !== "all") {
          urlParams.set("municipality", muni);
        }

        if (traffic.length > 0) {
          urlParams.set("trafficTypes", traffic.join(","));
        }

        window.history.replaceState(
          {},
          "",
          `?${urlParams.toString()}`
        );
      }

      setHasSearched(true);
    } catch (err) {
      console.error(err);

      setErrorMsg(
        "Ett fel uppstod vid hämtning av tidtabeller."
      );
    } finally {
      setIsLoading(false);
    }
  }
  function getSearchSummary() {
    const parts: string[] = [];

    const municipalityName =
      municipalityOptions.find(
        (m) => m.value === searchParams.municipality
      )?.label ?? searchParams.municipality;

    if (searchParams.lineNumber) {
      parts.push(`Linjenummer ${searchParams.lineNumber}`);
    }

    if (
      searchParams.municipality &&
      searchParams.municipality !== "all"
    ) {
      parts.push(`i ${municipalityName}`);
    }

    if (searchParams.trafficTypes.length > 0) {

      const labels = trafficTypes
        .filter((t) =>
          searchParams.trafficTypes.includes(t.Key)
        )
        .map((t) => t.Name);
      parts.push(labels.join(", "));
    }

    return {
      queryText: parts.join(" "),
      count: filtered.length,
    };
  }

  function resetFilters() {
    setLineNumber("");

    setSelectedMunicipality("all");

    setSelectedTrafficTypes(
      trafficTypes.map((t) => t.Key)
    );

    setAllTimetables([]);

    setHasSearched(false);

    setErrorMsg(null);

    setSearchParams({
      lineNumber: "",
      municipality: "",
      trafficTypes: [],
    });

    window.history.replaceState({}, "", window.location.pathname);

    setLiveMessage("");

    window.setTimeout(() => {
      setLiveMessage("Filtren har återställts.");
    }, 50);
  }

  const filtered = useMemo(() => allTimetables, [allTimetables]);

  useEffect(() => {
    if (!hasSearched || isLoading) return;

    const { queryText, count } = getSearchSummary();

    const nextMessage = errorMsg
      ? errorMsg
      : count === 0
        ? "Inga resultat hittades."
        : queryText
          ? `Din sökning på ${queryText} resulterade i ${count} träff${count === 1 ? "" : "ar"
          }.`
          : `Visar ${count} träff${count === 1 ? "" : "ar"}.`;

    setLiveMessage("");

    const timeoutId = window.setTimeout(() => {
      setLiveMessage(nextMessage);
    }, 50);

    return () => window.clearTimeout(timeoutId);
  }, [
    hasSearched,
    isLoading,
    errorMsg,
    filtered.length,
    searchParams,
  ]);

  return (
    <>
      <Box noBorder className="mb-0!">
        <Heading>Hitta din tidtabell</Heading>

        <p>
          Fyll i linjenummer, välj kommun eller trafikslag för att
          hitta rätt tidtabell.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();

            handleSearch();
          }}
        >
          <div className="flex flex-col mt-6 mb-6">
            <div className="flex gap-6 flex-col md:flex-row mb-6">
              <div className="flex-1">
                <InputFilterText
                  label="Vilket linjenummer ska du resa med?"
                  value={lineNumber}
                  type="text"
                  onChange={(e) => setLineNumber(e.target.value)}
                />
              </div>

              <div className="flex-1">
                <SelectFilterInput
                  name="municipality"
                  className="mb-0!"
                  label="Vilken kommun ska du resa inom?"
                  value={selectedMunicipality}
                  onChange={(e) =>
                    setSelectedMunicipality(e.target.value)
                  }
                  options={municipalityOptions}
                />
              </div>
            </div>

            <fieldset className="border-0 p-0 m-0">
              <legend className="mb-2 font-medium">
                Trafikslag
              </legend>

              <div className="flex flex-col md:flex-row md:flex-wrap gap-6">
                {trafficTypes.map((t) => (
                  <Checkbox
                    key={t.Key}
                    value={t.Key}
                    withLabel
                    variant="box"
                    id={`traffic-mode-${t.Key}`}
                    checked={selectedTrafficTypes.includes(t.Key)}
                    onCheckedChange={(checked) => {
                      setSelectedTrafficTypes((prev) =>
                        checked === true
                          ? [...prev, t.Key]
                          : prev.filter((v) => v !== t.Key)
                      );
                    }}
                  >
                    {t.Name}
                  </Checkbox>
                ))}
              </div>
            </fieldset>
          </div>

          <div className="flex justify-center">
            <div className="w-full text-left">
              <div className="flex flex-col md:flex-row gap-4">
                <Button
                  className="btn primary small"
                  type="submit"
                  size="large"
                  disabled={isLoading || !apiBaseUrl}
                  aria-busy={isLoading}
                >
                  {isLoading
                    ? "Laddar..."
                    : "Visa tidtabeller"}
                </Button>

                <Button
                  onClick={resetFilters}
                  type="button"
                  variant={"secondary"}
                  size="large"
                  className="btn"
                >
                  Återställ filter
                </Button>
              </div>
            </div>
          </div>
        </form>

        {errorMsg && (
          <Alert showIcon variant="error" className="mt-6">
            <AlertTitle>{errorMsg}</AlertTitle>
          </Alert>
        )}
      </Box>

      <div
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      >
        {liveMessage}
      </div>

      <div className="LineSearcResults mt-12 mb-12">
        {hasSearched ? (
          <>
            <Heading className="text-size-3x-large font-family-secondary font-semibold mb-2">
              Sökresultat för tidtabeller
            </Heading>

            {isLoading ? (
              <>
                <Spinner size={12} />{" "}
                <span>Laddar tidtabeller...</span>
              </>
            ) : filtered.length === 0 ? (
              <p>Inga resultat hittades.</p>
            ) : (
              <>
                <p className="mb-2 flex gap-3">
                  <Icon
                    name="search"
                    aria-hidden="true"
                    className="text-ot-red text-2xl min-w-4"
                  />

                  {(() => {
                    const { queryText, count } =
                      getSearchSummary();

                    return queryText ? (
                      <span>
                        Din sökning på{" "}
                        <strong className="sans-bold">
                          “{queryText}”
                        </strong>{" "}
                        resulterade i {count} träff
                        {count === 1 ? "" : "ar"}.
                      </span>
                    ) : (
                      <span>
                        Visar {count} träff
                        {count === 1 ? "" : "ar"}.
                      </span>
                    );
                  })()}
                </p>

                <ResultList timetables={filtered} />
              </>
            )}
          </>
        ) : (
          <>
            <Heading>Sökresultat för tidtabeller</Heading>

            <p className="mb-0 flex gap-3 flex-wrap items-start">
              <Icon
                name="info"
                aria-hidden="true"
                className="mt-2"
              />

              <span className="flex-1">
                Tips: Du kan söka direkt utan att fylla i
                något – då visar vi alla linjer.
              </span>
            </p>
          </>
        )}
      </div>
    </>
  );
}